import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { trigger } from 'swr';
import Loading from 'app/loading';
import { useGetCurrentMember } from 'auth/hooks';
import { Flex } from 'rebass';
import { BiX } from 'react-icons/bi';
import { Page, Content, SectionLabel, SectionPrompt, JournalCard, CardTitle, SpecText, ConfirmBar, BackButton, PrimaryButton, ErrorText, EmptyCopy } from '../styled';
import { DashboardTopNav } from '../chrome';
import Chevron from '../chevron';
import { useChildJournal, revalidateDashboard, serverErrorMessage, useMyAllChildJournals } from '../hooks';
import { specLine } from '../entitlements';
import {
  usePurchaseOptions,
  useSelection,
  useCreateDraftOrder,
  useDraftOrderPolling,
  getPendingDraftOrder,
  forgetPendingDraftOrder,
} from './hooks';
import { cancelShopifyDraftOrder } from './api';
import PurchaseRow from './purchase-row';
import ProductDetailModal from './product-detail-modal';
import StatusScreen from './status-screen';
import { GroupHeading, OptionList } from './styled';
import { useIsSideBarClosed } from 'ui/hooks';
import { useMobileCheckHook } from 'story-media/hooks';
import { Spinner } from 'app/loading/styled';

const GROUP_HEADINGS = {
  // The only group with its own heading — the design screenshot (which predates
  // upgrades shipping in this surface — see the red-dot annotation "Add-ons
  // without upgrade option, upgrades will be a follow up") doesn't show one for
  // the others, which read as a flat list.
  upgrade: 'Upgrade your plan',
};

const useQueryParams = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  return {
    title: params.get('title'),
    from: params.get('from') || 'my-books',
    // "Upgrade Bundle" on the book card links here with mode=upgrade so only the
    // upgrade group shows; otherwise ("Add-ons") the upgrade group is held back —
    // see where `groups` is filtered from `allGroups` below.
    mode: params.get('mode') === 'upgrade' ? 'upgrade' : 'add-ons',
  };
};

// `GET /v3/child_journals/:id/purchase_options` → pick, build a draft order, hand
// off to Shopify, then poll for payment — the whole flow from
// docs/in_app_purchasing_api_guide.md §2, scoped to one journal via the route.
const PurchaseScreen = () => {
  const { journalId } = useParams();
  const { title: queryTitle, from, mode } = useQueryParams();
  const { push } = useHistory();
  const { data: memberData } = useGetCurrentMember();
  const PURCHASE_URL = memberData?.data?.data?.attributes?.['baby-yearbook-purchase-url'];

  const { journal } = useChildJournal(journalId);
  const { groups: allGroups, isLoading, error, revalidate } = usePurchaseOptions(journalId);
  // "Upgrade Bundle" and "Add-ons" are deliberately separate purchases — the
  // upgrade group only shows in the former, feature/add_ons group only in the latter.
  const groups = useMemo(
    () => allGroups.filter(group => (mode === 'upgrade' ? group.key === 'upgrade' : (group.key === 'feature' || group.key === 'shipping'))),
    [allGroups, mode]
  );
  const options = useMemo(() => groups.flatMap(group => group.options), [groups]);
  const selection = useSelection(options);
  const { createDraftOrder, isCreating } = useCreateDraftOrder(journalId);
  const { journals: allJournals } = useMyAllChildJournals();
  const hasAssignedJournal = allJournals.some((journal) => journal?.state === "initialized");

  const isFromMobile = useMobileCheckHook()

  const pending = useMemo(() => getPendingDraftOrder(journalId), [journalId]);
  const [draftOrderId, setDraftOrderId] = useState(pending?.id || null);
  const [invoiceUrl, setInvoiceUrl] = useState(pending?.invoiceUrl || null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [detailOption, setDetailOption] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const handleCompleted = useCallback(() => {
    forgetPendingDraftOrder(journalId);
    // The journal's entitlements are the source of truth for what was bought —
    // never derived from the basket or draft-order line items (guide §6).
    trigger(`/v3/child_journals/${journalId}`);
    revalidateDashboard();
  }, [journalId]);

  const polling = useDraftOrderPolling(draftOrderId, { onCompleted: handleCompleted });
  const isSideBarClosed = useIsSideBarClosed();

  const title = queryTitle || journal?.title || 'Your book';
  const spec = specLine(journal?.entitlements);

  // Deliberately NOT keyed on `isCreating`: while the draft order is being
  // created, the member stays on the shelf (the Purchase button itself shows
  // the loading state — see below) and is sent straight to `invoiceUrl` the
  // moment it comes back, with no intermediate screen in between. `phase` only
  // starts mattering once a draft order actually exists to poll — i.e. a
  // member who has come BACK to a `pending` one, or the rare case where
  // creation succeeded but no invoice URL came back (see `handlePurchase`).
  //
  // The phase names below are historical and now read backwards from the
  // draft order's own status — `'open'` is what's shown for a `pending`
  // draft order ("Continue with your order?"), and `'waiting'` (the generic
  // processing screen) is what's shown once Shopify has actually acknowledged
  // it (`open`). Renaming the phases to match would be a larger, purely
  // cosmetic diff, so it's called out here instead.
  const TERMINAL_STATUSES = ['failed', 'unresolved'];
  const phase = draftOrderId
    ? TERMINAL_STATUSES.includes(polling.status)
      ? polling.status
      : polling.draftOrder?.status === 'pending'
        ? 'open'
        : polling.draftOrder?.status === 'open'
          ? 'open'
          : 'shelf'
    : 'shelf';

  // What to show on the "Continue with your order?" screen. The selection made
  // this session is the richer source (it still has `group`, for the
  // click-through detail view) — but a draft order resumed from a previous
  // session (see `getPendingDraftOrder`) starts with no local selection, so
  // fall back to the draft order's own line-item snapshot, which survives a
  // reload because the server does.
  const selectedOptions = useMemo(() => {
    if (selection.hasSelection) {
      return options
        .filter(option => selection.quantityOf(option.id) > 0)
        .map(option => ({ ...option, quantity: selection.quantityOf(option.id) }));
    }
    return (polling.draftOrder?.lineItems || []).map(item => ({
      id: item.sku || item.title,
      title: item.title,
      group: null,
      priceCents: Number(item['price-cents'] ?? item.price_cents) || 0,
      currency: polling.draftOrder?.currency || 'USD',
      quantity: item.quantity || 1,
    }));
    // eslint-disable-next-line 
  }, [selection.quantities, options, polling.draftOrder]);

  const handlePurchase = async () => {
    if (!selection.hasSelection || isCreating) return;
    setCheckoutError(null);
    const { ok, draftOrder, error: submitError } = await createDraftOrder(selection.lineItems);

    if (!ok || !draftOrder) {
      const status = submitError?.response?.status;
      if (status === 422) {
        setCheckoutError(serverErrorMessage(submitError, "That selection couldn't be completed."));
        // A 422 here usually means the cached shelf is stale (guide §5.2) —
        // refetch it; `useSelection` drops anything that comes back ineligible.
        revalidate();
      } else if (status === 502) {
        setCheckoutError('Something went wrong starting your purchase. Please try again.');
      } else if (status === 503) {
        setCheckoutError("We can't start purchases right now. Please try again later.");
      } else {
        setCheckoutError("We couldn't start your purchase. Please try again.");
      }
      return;
    }

    // Straight to Shopify — no intermediate screen. `createDraftOrder` has
    // already persisted the draft order (see `rememberPendingDraftOrder` in
    // ./hooks), so nothing here needs to go into local state before leaving
    // the page: doing that first only raced the navigation and could flash
    // 'creating' (or, once the first poll landed, 'open') for a frame before
    // the browser actually left. "Continue with your order?" is for when the
    // member comes BACK to a still-`pending` draft order, not for the moment
    // they're sent off to pay.
    if (draftOrder.invoiceUrl) {
      window.open(draftOrder.invoiceUrl, '_self', 'noopener');
      return;
    }

    // No invoice URL came back, which shouldn't happen per the API guide —
    // fall back to the polling screen rather than leaving the member on the
    // shelf with no feedback at all.
    setInvoiceUrl(draftOrder.invoiceUrl);
    setDraftOrderId(draftOrder.id);
  };

  const handleBack = () => push(`/account/${from}`);
  const handleDone = () => push(`/account/${from}`);
  const handleRetry = () => {
    forgetPendingDraftOrder(journalId);
    setDraftOrderId(null);
    setInvoiceUrl(null);
  };
  const handleReopenInvoice = () => invoiceUrl && window.open(invoiceUrl, '_self', 'noopener');

  // "Cancel order" on the "Continue with your order?" screen. Best-effort on
  // the server (an abandoned, unpaid draft order is harmless — guide §13 —
  // and the member has already said they don't want it), but a failure stays
  // on screen with a message rather than silently claiming it worked.
  const handleCancelOrder = async () => {
    if (!draftOrderId || isCancelling) return;
    setIsCancelling(true);
    setCancelError(null);
    try {
      await cancelShopifyDraftOrder(draftOrderId);
      forgetPendingDraftOrder(journalId);
      setDraftOrderId(null);
      setInvoiceUrl(null);
      push(`/account/${from}`);
    } catch (err) {
      setCancelError(serverErrorMessage(err, "We couldn't cancel that order. Please try again."));
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Page>
      <DashboardTopNav PURCHASE_URL={PURCHASE_URL || ''} onRedeemGift={() => push('/redeem-gift-certificate')} />
      <Content style={{
        ...((!hasAssignedJournal || isFromMobile) ? {} : { margin: "auto" }),
        maxWidth: 600,
        padding: "30px 24px"
      }}>
        {phase !== 'shelf' ? (
          <StatusScreen
            phase={phase}
            invoiceUrl={invoiceUrl}
            backedOff={polling.backedOff}
            title={title}
            spec={spec}
            selectedOptions={selectedOptions}
            onCheckNow={polling.checkNow}
            onReopenInvoice={handleReopenInvoice}
            onRetry={handleRetry}
            onDone={handleDone}
            onShowDetail={setDetailOption}
          />
        ) : (
          <>
            <Flex flexDirection="column" sx={{ gap: 1 }}>
              <SectionLabel>{mode === 'upgrade' ? 'Upgrade Bundle' : 'Purchase Add-ons'}</SectionLabel>
              <SectionPrompt>
                {mode === 'upgrade'
                  ? 'Move this book to a better plan:'
                  : 'Shop extra add-ons to add to this book:'}
              </SectionPrompt>
            </Flex>

            <JournalCard>
              <CardTitle>{title}</CardTitle>
              {!!spec && <SpecText>{spec}</SpecText>}
            </JournalCard>

            {isLoading && <Loading local />}

            {!isLoading && !!error && (
              <>
                <ErrorText>We couldn&rsquo;t load the add-ons for this book.</ErrorText>
                <PrimaryButton inline type="button" onClick={revalidate}>
                  Try again
                </PrimaryButton>
              </>
            )}

            {(!isLoading && !error && !groups.length) && (
              <EmptyCopy>
                {mode === 'upgrade'
                  ? 'This book has no further upgrades available.'
                  : 'There’s nothing left to add to this book right now.'}
              </EmptyCopy>
            )}

            {!isLoading &&
              !error &&
              groups.map(group => (
                <React.Fragment key={group.key}>
                  {/* Redundant in the dedicated Upgrade Bundle view, where the
                      page heading above already says as much. */}
                  {mode !== 'upgrade' && !!GROUP_HEADINGS[group.key] && (
                    <GroupHeading>{GROUP_HEADINGS[group.key]}</GroupHeading>
                  )}
                  <OptionList>
                    {group.options.map(option => (mode === 'upgrade' && option.eligible === false ? null : (
                      <PurchaseRow
                        key={option.id}
                        option={option}
                        quantity={selection.quantityOf(option.id)}
                        selected={selection.isSelected(option.id)}
                        onToggle={selection.toggle}
                        onQuantityChange={selection.setQuantity}
                        onShowDetail={setDetailOption}
                      />
                    )))}
                  </OptionList>
                </React.Fragment>
              ))}

            {!!checkoutError && <ErrorText>{checkoutError}</ErrorText>}
          </>
        )}
      </Content>

      {!!cancelError && <ErrorText style={{ textAlign: 'center', padding: '0 24px' }}>{cancelError}</ErrorText>}

      {(phase === 'shelf' || phase === 'open') && (
        <ConfirmBar style={(!hasAssignedJournal || isFromMobile) ? {} : { width: isSideBarClosed ? '100%' : 'auto', paddingLeft: isSideBarClosed ? "100px" : "30px", margin: isSideBarClosed ? "0 auto" : "0 0 0 260px" }}>
          {phase === 'open' ? (
            <>
              <BackButton type="button" disabled={isCancelling} onClick={handleCancelOrder}>
                <BiX size={18} />
                {isCancelling ? 'Cancelling…' : 'Cancel order'}
              </BackButton>
              <PrimaryButton inline type="button" onClick={handleReopenInvoice}>
                Resume checkout
                <Chevron />
              </PrimaryButton>
            </>
          ) : (
            <>
              <BackButton type="button" onClick={handleBack}>
                <Chevron direction="left" />
                Back
              </BackButton>
              <PrimaryButton inline type="button" disabled={!selection.hasSelection || isCreating} onClick={handlePurchase}>
                {isCreating ? 'Starting' : 'Purchase'}
                {isCreating ? <Spinner style={{ marginLeft: 8 }} color="#fff" height="25px" width="25px" /> : <Chevron />}
              </PrimaryButton>
            </>
          )}
        </ConfirmBar>
      )}

      {!!detailOption && <ProductDetailModal mode={mode} option={detailOption} onClose={() => setDetailOption(null)} />}
    </Page>
  );
};

export default PurchaseScreen;
